import type { ReportSectorObservation } from './types';
import { emissionsStatus, numOrNull, reportValue } from './valueHelpers';

export interface PrecalcRow {
  id: number;
  geofips: number;
  location_name: string;
  year: number;
  calculation_date: string | null;
  ldv_vmt: number | null;
  ldv_vmt_source: string | null;
  ldv_emission_factor: number | null;
  ldv_emission_factor_source: string | null;
  ldv_emissions: number | null;
  ldv_notes: string | null;
  hdv_emissions: number | null;
  hdv_data_source: string | null;
  elec_residential_kwh: number | null;
  elec_commercial_kwh: number | null;
  elec_carbon_intensity: number | null;
  elec_carbon_intensity_source: string | null;
  elec_provider: string | null;
  elec_residential_emissions: number | null;
  elec_commercial_emissions: number | null;
  elec_total_emissions: number | null;
  elec_residential_source: string | null;
  elec_commercial_source: string | null;
  ng_residential_therms: number | null;
  ng_commercial_therms: number | null;
  ng_residential_emissions: number | null;
  ng_commercial_emissions: number | null;
  ng_total_emissions: number | null;
  ng_residential_source: string | null;
  ng_commercial_source: string | null;
  waste_tons: number | null;
  waste_tons_source: string | null;
  waste_emission_factor: number | null;
  waste_emission_factor_source: string | null;
  waste_emissions: number | null;
  offroad_emissions: number | null;
  offroad_data_source: string | null;
  agriculture_emissions: number | null;
  total_emissions: number | null;
}

export const SECTOR_SUM_CODES = [
  'ldv',
  'hdv',
  'elec_res',
  'elec_com',
  'ng_res',
  'ng_com',
  'waste',
  'offroad',
  'agriculture',
] as const;

function obs(
  code: string,
  label: string,
  emissionsCol: string,
  emissions: number | null,
  sourceStatusCol: string | null,
  sourceStatus: string | null,
  activityCol?: string,
  activity?: number | null,
  activityUnit?: string,
  factorCol?: string,
  factor?: number | null,
  factorUnit?: string,
  notes?: string,
  rowYear?: number
): ReportSectorObservation {
  const { status, publicationStatus } = emissionsStatus(emissions, sourceStatus);
  return {
    sectorCode: code,
    sectorLabel: label,
    emissionsMt: reportValue(emissions, 'MT CO2e', status, {
      sourceTable: 'ghg_inventory_precalculated',
      sourceColumn: emissionsCol,
      calculationRule: sourceStatusCol ? `status from ${sourceStatusCol}` : undefined,
    }, publicationStatus),
    activity:
      activityCol && activity != null
        ? reportValue(activity, activityUnit ?? '', emissionsStatus(activity, sourceStatus).status, {
            sourceTable: 'ghg_inventory_precalculated',
            sourceColumn: activityCol,
          })
        : activityCol
          ? reportValue(null, activityUnit ?? '', 'missing', {
              sourceTable: 'ghg_inventory_precalculated',
              sourceColumn: activityCol,
            })
          : undefined,
    activityUnit,
    emissionFactor:
      factorCol && factor != null
        ? reportValue(factor, factorUnit ?? '', 'modeled', {
            sourceTable: 'ghg_inventory_precalculated',
            sourceColumn: factorCol,
          })
        : undefined,
    emissionFactorUnit: factorUnit,
    notes,
    databaseField: emissionsCol,
    sourceStatusText: sourceStatus,
    sourceYear: rowYear,
  };
}

export function mapPrecalcToSectorObservations(row: PrecalcRow): ReportSectorObservation[] {
  const y = row.year;
  return [
    obs('ldv', 'Light-duty vehicles', 'ldv_emissions', numOrNull(row.ldv_emissions), 'ldv_vmt_source', row.ldv_vmt_source, 'ldv_vmt', numOrNull(row.ldv_vmt), 'VMT', 'ldv_emission_factor', numOrNull(row.ldv_emission_factor), 'MT CO2e/mile', row.ldv_notes ?? undefined, y),
    obs('hdv', 'Heavy-duty vehicles', 'hdv_emissions', numOrNull(row.hdv_emissions), 'hdv_data_source', row.hdv_data_source, undefined, undefined, undefined, undefined, undefined, undefined, undefined, y),
    obs('elec_res', 'Residential electricity', 'elec_residential_emissions', numOrNull(row.elec_residential_emissions), 'elec_residential_source', row.elec_residential_source, 'elec_residential_kwh', numOrNull(row.elec_residential_kwh), 'kWh', 'elec_carbon_intensity', numOrNull(row.elec_carbon_intensity), 'g CO2e/kWh', undefined, y),
    obs('elec_com', 'Commercial electricity', 'elec_commercial_emissions', numOrNull(row.elec_commercial_emissions), 'elec_commercial_source', row.elec_commercial_source, 'elec_commercial_kwh', numOrNull(row.elec_commercial_kwh), 'kWh', 'elec_carbon_intensity', numOrNull(row.elec_carbon_intensity), 'g CO2e/kWh', undefined, y),
    obs('ng_res', 'Residential natural gas', 'ng_residential_emissions', numOrNull(row.ng_residential_emissions), 'ng_residential_source', row.ng_residential_source, 'ng_residential_therms', numOrNull(row.ng_residential_therms), 'therms', undefined, undefined, undefined, undefined, y),
    obs('ng_com', 'Commercial natural gas', 'ng_commercial_emissions', numOrNull(row.ng_commercial_emissions), 'ng_commercial_source', row.ng_commercial_source, 'ng_commercial_therms', numOrNull(row.ng_commercial_therms), 'therms', undefined, undefined, undefined, undefined, y),
    obs('waste', 'Solid waste', 'waste_emissions', numOrNull(row.waste_emissions), 'waste_tons_source', row.waste_tons_source, 'waste_tons', numOrNull(row.waste_tons), 'tons', 'waste_emission_factor', numOrNull(row.waste_emission_factor), 'MT CO2e/ton', undefined, y),
    obs('offroad', 'Off-road', 'offroad_emissions', numOrNull(row.offroad_emissions), 'offroad_data_source', row.offroad_data_source, undefined, undefined, undefined, undefined, undefined, undefined, undefined, y),
    obs('agriculture', 'Agriculture', 'agriculture_emissions', numOrNull(row.agriculture_emissions), null, null, undefined, undefined, undefined, undefined, undefined, undefined, 'Optional municipal sector; not required for ClimatePlans city total comparability', y),
    {
      sectorCode: 'water_wastewater',
      sectorLabel: 'Water / wastewater',
      emissionsMt: reportValue(null, 'MT CO2e', 'not_modeled', {
        sourceTable: 'n/a',
        calculationRule: 'Not included in the current ClimatePlans inventory model',
      }),
      notes: 'Not included in the current ClimatePlans inventory model',
      databaseField: undefined,
      sourceStatusText: 'not_modeled',
      sourceYear: y,
    },
  ];
}

export interface SectorSumResult {
  sumMt: number;
  includedSectors: string[];
  excludedSectors: string[];
  completenessLabel: 'complete_available_sectors' | 'partial_available_sectors';
}

export function sumAvailableSectorEmissions(sectors: ReportSectorObservation[]): SectorSumResult {
  const included: string[] = [];
  const excluded: string[] = [];
  let sum = 0;
  for (const code of SECTOR_SUM_CODES) {
    const s = sectors.find((x) => x.sectorCode === code);
    if (!s) continue;
    if (s.emissionsMt.value != null && s.emissionsMt.status !== 'missing' && s.emissionsMt.status !== 'not_modeled') {
      included.push(s.sectorLabel);
      sum += s.emissionsMt.value;
    } else {
      excluded.push(s.sectorLabel);
    }
  }
  return {
    sumMt: sum,
    includedSectors: included,
    excludedSectors: excluded,
    completenessLabel: excluded.length === 0 ? 'complete_available_sectors' : 'partial_available_sectors',
  };
}

/** @deprecated use sumAvailableSectorEmissions */
export function sumSectorEmissions(sectors: ReportSectorObservation[]): number {
  return sumAvailableSectorEmissions(sectors).sumMt;
}

export function findDominantSector(sectors: ReportSectorObservation[]): string | null {
  const candidates = sectors.filter(
    (s) =>
      SECTOR_SUM_CODES.includes(s.sectorCode as typeof SECTOR_SUM_CODES[number]) &&
      s.emissionsMt.value != null &&
      s.emissionsMt.status !== 'missing'
  );
  if (!candidates.length) return null;
  return candidates.reduce((a, b) => ((a.emissionsMt.value ?? 0) >= (b.emissionsMt.value ?? 0) ? a : b)).sectorLabel;
}

export function buildingTransportBalance(sectors: ReportSectorObservation[]): { buildingMt: number | null; transportMt: number | null; comparable: boolean } {
  const need = ['elec_res', 'elec_com', 'ng_res', 'ng_com', 'ldv', 'hdv'];
  const vals: Record<string, number | null> = {};
  for (const code of need) {
    const s = sectors.find((x) => x.sectorCode === code);
    vals[code] = s && s.emissionsMt.value != null && s.emissionsMt.status !== 'missing' ? s.emissionsMt.value : null;
  }
  const allPresent = need.every((c) => vals[c] != null);
  if (!allPresent) return { buildingMt: null, transportMt: null, comparable: false };
  const buildingMt = (vals.elec_res ?? 0) + (vals.elec_com ?? 0) + (vals.ng_res ?? 0) + (vals.ng_com ?? 0);
  const transportMt = (vals.ldv ?? 0) + (vals.hdv ?? 0);
  return { buildingMt, transportMt, comparable: true };
}
